import { roundToPlaces } from "../../utils/roundToPlaces.js";
import type { CalculatedOrderDiscounts } from "../CalculatedOrderDiscounts.js";
import { Country } from "../Country.js";
import type { Customer } from "../Customer.js";
import type { OrderInput } from "../Order.js";
import { PricingLevel } from "../PricingLevel.js";
import type { Subcategory } from "../Subcategory.js";
import { fathersFamilyDiscountPercentage, fathersFamilyDiscountProductAmount } from "../fathersFamilyDiscount.js";
import { calculatePriceWithTax } from "./calculatePriceWithTax.js";
import { isCountryInEuropeanUnion } from "./isCountryInEuropeanUnion.js";

export function calculateFathersFamilyDiscounts(
	order: Pick<OrderInput, "products" | "deliveryAddress" | "couponCode">,
	customer: Customer | null,
	subcategories: Pick<Subcategory, "id" | "urlSlug" | "eligibleForB2cDiscount">[],
): CalculatedOrderDiscounts {
	const discounts: CalculatedOrderDiscounts = {
		discountsByTaxRate: [],
		freeDelivery: false,
	};

	if (customer === null) {
		return discounts;
	}

	if (customer.pricingLevel !== PricingLevel.B2c || !isCountryInEuropeanUnion(order.deliveryAddress.country)) {
		return discounts;
	}

	const eligibleSubcategoryIds = new Set(
		subcategories.filter(({ eligibleForB2cDiscount }) => eligibleForB2cDiscount).map(({ id }) => id),
	);
	const discountedProducts = order.products.filter((product) => eligibleSubcategoryIds.has(product.info.subcategoryId));
	const discountedProductsQuantity = discountedProducts.reduce((sum, product) => sum + product.quantity, 0);

	discounts.freeDelivery = discountedProductsQuantity >= 1;

	if (
		discountedProductsQuantity >= fathersFamilyDiscountProductAmount &&
		order.deliveryAddress.country === Country.Czechia
	) {
		const byTaxRate = new Map<number, number>();
		for (const product of discountedProducts) {
			const priceWithoutTax = byTaxRate.get(product.info.price.taxRate) ?? 0;
			byTaxRate.set(product.info.price.taxRate, priceWithoutTax + product.info.price.withoutTax * product.quantity);
		}
		for (const [taxRate, price] of byTaxRate) {
			const discountedPriceWithoutTax = roundToPlaces(price * (fathersFamilyDiscountPercentage / 100), 2);
			discounts.discountsByTaxRate.push({
				taxRate,
				withoutTax: discountedPriceWithoutTax,
				withTax: calculatePriceWithTax(discountedPriceWithoutTax, taxRate),
			});
		}
	}

	return discounts;
}
